import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { UnitType } from '@racemap/utilities/consts/events';
import { formatOffsetInMeterBasedOnUnitType } from '@racemap/utilities/formatting';
import type { TeamTarget } from '@racemap/utilities/types/events';
import { type FC, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { Center } from '../../BasicComponents/MetaComponent';
import { Progress } from '../Progress';

const PANEL_KEY = 'main';

interface Props {
  teamTarget: TeamTarget;
  totalDistance: number;
  unit?: UnitType;
  defaultOpen?: boolean;
}

export const TeamTargetPanel: FC<Props> = ({
  teamTarget,
  totalDistance,
  unit = UnitType.METRIC,
  defaultOpen = false,
}) => {
  const [activeKey, setActiveKey] = useState(defaultOpen ? PANEL_KEY : '');
  const isExpanded = activeKey === PANEL_KEY;

  const handleHeaderClick = () => {
    if (isExpanded) return setActiveKey('');
    setActiveKey(PANEL_KEY);
  };

  return (
    <Container>
      <Center>
        <Panel activeKey={activeKey}>
          <TargetPanelHeader
            teamTarget={teamTarget}
            totalDistance={totalDistance}
            expanded={isExpanded}
            onClick={handleHeaderClick}
          />
          <Accordion.Collapse eventKey={PANEL_KEY}>
            <Body>
              <Current>Current</Current>
              <Goal>Goal</Goal>
              <NumberCurrent>
                {formatOffsetInMeterBasedOnUnitType(totalDistance, unit)}
              </NumberCurrent>
              <NumberGoal>
                {formatOffsetInMeterBasedOnUnitType(teamTarget.distance, unit)}
              </NumberGoal>
            </Body>
          </Accordion.Collapse>
        </Panel>
      </Center>
    </Container>
  );
};

function TargetPanelHeader({
  teamTarget,
  totalDistance,
  expanded,
  onClick,
}: {
  teamTarget: TeamTarget;
  totalDistance: number;
  expanded: boolean;
  onClick: () => void;
}) {
  const now = Math.floor((totalDistance / teamTarget.distance) * 100);

  return (
    <Header onClick={onClick} expanded={expanded}>
      <HeaderTitle>{teamTarget.description} </HeaderTitle>
      <Progress now={now} withLabel variant={now >= 100 ? 'success' : ''} />
    </Header>
  );
}

const Container = styled.div`
  padding: 10px;
  width: 100%;
`;

const Panel = styled(Accordion)`
  min-width: 300px;

  .show ~ div {
    border-radius: 0.25rem 0.25rem 0 0;
  }
`;

const Body = styled.div`
  padding: 10px 22px;
  background: #f2f2f2;
  border: solid 1px ${RacemapColors.LightGray};
  border-top: unset;
  border-radius: 0 0 0.25rem 0.25rem;
  display: grid;
  grid-template:
    [row1-start] 'cur goal' [row1-end]
    [row2-start] 'num_cur num_goal' [row2-start]
    / 1fr 1fr;
  grid-gap: 3px;
`;

const Current = styled.div`
  grid-area: cur;
  color: ${RacemapColors.DarkGray};
`;
const Goal = styled.div`
  grid-area: goal;
  color: ${RacemapColors.DarkGray};
`;
const NumberCurrent = styled.div`
  grid-area: num_cur;
  font-size: 1.2em;
  font-weight: 500;
`;
const NumberGoal = styled.div`
  grid-area: num_goal;
`;

const Header = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  background: white;
  border: solid 1px ${RacemapColors.LightGray};
  height: 100%;
  padding: 15px;
  border-radius: ${({ expanded }) => (expanded ? '0.25rem 0.25rem 0 0' : '0.25rem')};
`;

const HeaderTitle = styled.h3`
  font-size: 1.04em;
  margin-bottom: 10px;
`;
